import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { previewCache } from "@/lib/previewCache";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, redirectPath?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Use refs to track current state without causing re-subscriptions
  const currentUserIdRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        
        const newUserId = newSession?.user?.id ?? null;
        
        if (initialLoadDoneRef.current) {
          if (event === 'INITIAL_SESSION') return;
          
          if (event === 'TOKEN_REFRESHED' && currentUserIdRef.current === newUserId) {
            return;
          }
        }
        
        // Handle persistent session logic
        if (newSession) {
          // Si hay sesión, guardamos el timestamp de última actividad
          localStorage.setItem('auth_last_activity', Date.now().toString());
        }

        if (
          currentUserIdRef.current !== newUserId || 
          event === 'SIGNED_IN' || 
          event === 'SIGNED_OUT'
        ) {
          // Wipe preview cache + abort in-flight signed-URL fetches whenever
          // the identity boundary changes (SIGNED_OUT or account switch).
          if (event === 'SIGNED_OUT' || (currentUserIdRef.current && currentUserIdRef.current !== newUserId)) {
            try { previewCache.clear(); } catch { /* ignore */ }
          }
          currentUserIdRef.current = newUserId;
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }
        
        setLoading(false);
        initialLoadDoneRef.current = true;
      }
    );

    // Inactivity check: 30 minutes
    const INACTIVITY_LIMIT = 30 * 60 * 1000;
    const activityInterval = setInterval(() => {
      const lastActivity = localStorage.getItem('auth_last_activity');
      if (lastActivity && Date.now() - parseInt(lastActivity) > INACTIVITY_LIMIT) {
        console.log("Session expired due to inactivity");
        supabase.auth.signOut();
      }
    }, 60000); // Check every minute

    // Activity listeners — throttled with requestAnimationFrame + 1s gate
    // to avoid 'mousemove' handler violations on long sessions.
    let rafScheduled = false;
    let lastWrite = 0;
    const updateActivity = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        rafScheduled = false;
        const now = Date.now();
        if (now - lastWrite < 1000) return;
        lastWrite = now;
        if (currentUserIdRef.current) {
          localStorage.setItem('auth_last_activity', now.toString());
        }
      });
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'] as const;
    activityEvents.forEach((e) =>
      window.addEventListener(e, updateActivity, { passive: true })
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (mounted && !initialLoadDoneRef.current) {
        const userId = initialSession?.user?.id ?? null;
        currentUserIdRef.current = userId;
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        setLoading(false);
        initialLoadDoneRef.current = true;
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearInterval(activityInterval);
      activityEvents.forEach((e) =>
        window.removeEventListener(e, updateActivity)
      );
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string, redirectPath?: string) => {
    const redirectUrl = new URL(redirectPath || "/", window.location.origin).href;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    // Abort in-flight preview requests and wipe cached signed URLs BEFORE
    // clearing the session so no request survives into the next login.
    try {
      previewCache.clear();
    } catch (e) {
      console.warn("Preview cache clear on signOut failed:", e);
    }
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("SignOut API error (clearing local state anyway):", e);
    }
    // Always clear local state regardless of API result
    currentUserIdRef.current = null;
    setSession(null);
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
